import type {
  InteractionClient,
  TimelineClient,
} from '@packages/systems/ix3/core';
import {TransformBuilder} from '@packages/systems/ix3/core';
import {
  IX3TransformEngine,
  type TransformBaseContext,
} from '@packages/systems/ix3/transforms';
import {
  type WFTransformsBuilder,
  CORE_PLUGIN_INFO,
} from '@packages/systems/ix3-wf-plugins/shared';
import type {ClassNameMap} from '@packages/systems/style/types';
import type {VariableEntries} from '@packages/systems/ix2/shared-utils';

import {getTimelinesForInteractions} from './filter-utils';
import {
  transformClassTargetValue,
  transformClassActionSelectors,
  transformVariableActionData,
  transformCustomPropertyKeys,
} from './transform-utils';
import {resolveVariableRefsInProperties} from './animation-variable-utils';
import {
  filterAllowedTimelineProperties,
  type ActionPropertyFilterOptions,
} from './actionPropertyAllowlist';

type PublishMeta = {
  classNameMap: ClassNameMap;
  variables: VariableEntries;
};

type IX3PublishContext = TransformBaseContext<
  {environment: 'publish'} & PublishMeta
>;

// Safe to reuse: transform() resets pathTracker and errorCollector per call.
const publishEngine = createPublishTransformEngine();

/**
 * Filters site-wide IX3 data to a single component and transforms it
 * for the published runtime. Only component-scoped interactions
 * (scope.type === 'component') matching the given root element ID
 * are included. Referenced timelines are collected and deduped.
 *
 * Transform pipeline matches the publish config in
 * entrypoints/server/domains/ix3/publishing/getIx3Js.ts — strips
 * server fields, resolves class targets, resolves CSS variables,
 * and applies size optimizations.
 *
 * Interactions are dropped if:
 *   - `deleted: true`, or
 *   - every referenced timeline is deleted or has `actions.length === 0`.
 *
 * The second rule is deliberately stricter than the published-site
 * runtime, which tolerates no-op interactions because GSAP is loaded
 * from a CDN unconditionally. Devlink bundles GSAP per-export via
 * `getGsapScripts`, which itself requires at least one non-empty
 * timeline-actions array before registering the `core` plugin. Applying
 * the same rule here keeps `hasIx3` (used by the server's engine file
 * filter) and `metadata.gsapPluginKeys` (used by the engine-bundle
 * emit path) in lock-step — otherwise an interaction pointing only at
 * empty or deleted timelines would ship `ix3-interactions.tsx` into the
 * export without `devlink-ix3.js` / `devlink-gsap.js`, and the
 * customer's build would fail on `await import('./devlink-gsap')`.
 * See STYL-2280 review feedback.
 */
export function getComponentIx3Interactions(
  ix3Interactions: InteractionClient[],
  ix3Timelines: TimelineClient[],
  componentRootId: string,
  classNameMap: ClassNameMap,
  variables: VariableEntries,
  allowlist?: ActionPropertyFilterOptions
): {interactions: InteractionClient[]; timelines: TimelineClient[]} {
  // 1. Filter: keep only active, component-scoped interactions for this component
  const scopedInteractions: InteractionClient[] = [];

  for (const interaction of ix3Interactions) {
    if (interaction.deleted) continue;
    if (
      interaction.scope?.type === 'component' &&
      interaction.scope.componentId === componentRootId
    ) {
      scopedInteractions.push(interaction);
    }
  }

  if (scopedInteractions.length === 0) {
    return {interactions: [], timelines: []};
  }

  // 2. Drop interactions whose referenced timelines are all deleted
  //    or have no actions — see JSDoc for why Devlink is stricter than
  //    the published-site runtime here.
  const timelineById = new Map<string, TimelineClient>();
  for (const timeline of ix3Timelines) {
    timelineById.set(timeline.id, timeline);
  }
  const hasRunnableTimeline = (interaction: InteractionClient): boolean =>
    interaction.timelineIds.some((timelineId) => {
      const timeline = timelineById.get(timelineId);
      return (
        timeline !== undefined &&
        !timeline.deleted &&
        timeline.actions.length > 0
      );
    });
  const filteredInteractions = scopedInteractions.filter(hasRunnableTimeline);

  if (filteredInteractions.length === 0) {
    return {interactions: [], timelines: []};
  }

  // 3. Collect: gather only the timelines referenced by filtered interactions
  const filteredTimelines = getTimelinesForInteractions(
    filteredInteractions,
    ix3Timelines
  ).filter((t) => !t.deleted);

  // 4. Transform: strip server fields, resolve class targets and CSS variables
  const context: IX3PublishContext = {
    environment: 'publish',
    classNameMap,
    variables,
    propertiesTransform: (properties) =>
      resolveVariableRefsInProperties(properties, (id) => variables[id]),
  };

  const {data, errors} = publishEngine.transform(
    {
      ix3Interactions: filteredInteractions,
      ix3Timelines: filteredTimelines,
    },
    context
  );

  if (errors.length > 0) {
    console.warn('[IX3 Transform] Errors:', errors);
  }

  return {
    interactions: data.ix3Interactions ?? [],
    timelines: filterAllowedTimelineProperties(
      data.ix3Timelines ?? [],
      allowlist
    ),
  };
}

/**
 * Creates an IX3TransformEngine configured identically to the publish
 * pipeline in entrypoints/server/domains/ix3/publishing/getIx3Js.ts.
 * Keep in sync with createPublishEngine() in that file.
 */
function createPublishTransformEngine(): IX3TransformEngine<IX3PublishContext> {
  const engine = new IX3TransformEngine<IX3PublishContext>({
    coreConfig: {
      // `componentSeed` (the Designer's settings baseline) is not listed: the
      // transform engine's `stripInteractionFields` already drops it for every
      // publish, so this list stays identical to the site publish pipeline's.
      stripFields: ['createdOn', 'updatedOn', 'siteId', 'pageId', 'version'],
      defaults: {
        timing: {duration: 0.5, delay: 0},
        trigger: {delay: 0},
        scrollTrigger: {start: 'top center', end: 'bottom center'},
      },
      optimizations: {
        removeEmptyArrays: true,
        removeNullValues: true,
        removeEmptyObjects: true,
        // timelineIds is a required structural field the runtime reads
        // unconditionally; never strip it, even for action-less interactions.
        // assignedGroupId must survive as an explicit `null` ("unassigned,
        // play nothing"). Stripping it would publish as if the field were
        // missing, which the runtime reads as the legacy "play every
        // timeline" state. `undefined` is still omitted by the transform.
        // assignedInstances `[]` is the same kind of explicit "none": the
        // author narrowed the trigger to no popover, and stripping it would
        // publish `undefined`, which the runtime reads as every popover.
        preserveKeys: ['timelineIds', 'assignedGroupId', 'assignedInstances'],
      },
    },
    enableErrorCollection: true,
    enablePathTracking: true,
  });

  const transforms: WFTransformsBuilder<IX3PublishContext> =
    new TransformBuilder(CORE_PLUGIN_INFO);

  transforms.addTargetTransform('class', (data, context) => {
    const transformed = transformClassTargetValue(
      data[1],
      context.classNameMap
    );
    if (transformed !== undefined) {
      data[1] = transformed;
    }
  });

  transforms.addActionTransform('class', (data, context) => {
    const dataClass = data.class as Record<string, unknown> | undefined;
    if (dataClass) {
      const transformedSelectors = transformClassActionSelectors(
        data,
        context.classNameMap
      );
      if (transformedSelectors !== undefined) {
        dataClass.selectors = transformedSelectors;
      }
    }
  });

  transforms.addActionTransform('variable', (data, context) => {
    transformVariableActionData(data, (id) => context.variables[id]);
  });

  transforms.addActionTransform('custom-property', (data) =>
    transformCustomPropertyKeys(data)
  );

  engine.registerPlugin(transforms.buildTransform());

  return engine;
}
